/**
 * Rapports calculés par le serveur (module `reports`) sur une période Du/Au : totaux, bilan par cycle
 * (masse salariale, recouvrement des frais), dépenses par catégorie, évolution dans le temps, prévu vs réalisé.
 */
import { http } from "./http";
import type { CycleCode } from "@/types";

export interface ReportPeriod {
  from: string;
  to: string;
}

export interface FinancialSummary {
  totalRecettes: number;
  totalDepenses: number;
  resultat: number;
  marge: number;
}

export interface FeesFigures {
  expected: number;
  collected: number;
  outstanding: number;
  collectionRate: number;
}

export interface OverviewReport {
  finance: FinancialSummary;
  school: { schoolYear: string | null; activeEnrollments: number; teachers: number; staff: number };
  fees: FeesFigures;
}

export interface CycleReport {
  cycleId: string;
  cycleCode: CycleCode;
  label: string;
  finance: FinancialSummary;
  activeEnrollments: number;
  monthlyPayroll: number;
  staffPayroll: number;
  teacherPayroll: number;
  payrollHeadcount: number;
  fees: FeesFigures;
}

export interface CategoryReportItem {
  categoryId: string;
  name: string;
  type: "RECETTE" | "DEPENSE";
  cycleId: string;
  total: number;
  count: number;
  share: number;
}

export interface CashflowPoint {
  period: string;
  recettes: number;
  depenses: number;
  net: number;
  cumulative: number;
}

export interface CashflowReport {
  granularity: "month" | "day";
  points: CashflowPoint[];
}

export interface BudgetCycleLine {
  cycleId: string;
  cycleCode: CycleCode;
  monthlyBudget: number;
  budget: number;
  spent: number;
  rate: number | null;
}

export interface BudgetCharge {
  chargeId: string;
  name: string;
  icon: string;
  groupName: string;
  monthlyBudget: number;
  budget: number;
  spent: number;
  rate: number | null;
  cycles: BudgetCycleLine[];
}

export interface BudgetReport {
  months: number;
  charges: BudgetCharge[];
  total: { budget: number; spent: number; rate: number | null; unassignedSpent: number };
}

const range = (p: ReportPeriod) => ({ dateFrom: p.from || undefined, dateTo: p.to || undefined });

export async function getOverview(period: ReportPeriod): Promise<OverviewReport> {
  return http.get<OverviewReport>("/reports/overview", range(period));
}

export async function getByCycle(period: ReportPeriod): Promise<{ cycles: CycleReport[]; total: FinancialSummary }> {
  const res = await http.get<{
    cycles: Array<Omit<CycleReport, "cycleId" | "cycleCode" | "label"> & { cycle: { id: number; code: CycleCode; label: string } }>;
    total: FinancialSummary;
  }>("/reports/by-cycle", range(period));
  return {
    total: res.total,
    cycles: res.cycles.map(({ cycle, ...rest }) => ({ ...rest, cycleId: String(cycle.id), cycleCode: cycle.code, label: cycle.label })),
  };
}

export async function getByCategory(period: ReportPeriod, type?: "RECETTE" | "DEPENSE"): Promise<CategoryReportItem[]> {
  const res = await http.get<{ categories: Array<Omit<CategoryReportItem, "categoryId" | "cycleId"> & { categoryId: number; cycleId: number }> }>(
    "/reports/by-category",
    { ...range(period), type },
  );
  return res.categories.map((c) => ({ ...c, categoryId: String(c.categoryId), cycleId: String(c.cycleId) }));
}

/** Plus de 62 jours : un point par mois ; sinon un point par jour. */
export function granularityFor(period: ReportPeriod): "month" | "day" {
  if (!period.from || !period.to) return "month";
  const days = (Date.parse(period.to) - Date.parse(period.from)) / 86_400_000 + 1;
  return days > 62 ? "month" : "day";
}

export async function getCashflow(period: ReportPeriod): Promise<CashflowReport> {
  return http.get<CashflowReport>("/reports/cashflow", { ...range(period), granularity: granularityFor(period) });
}

export async function getBudget(period: ReportPeriod): Promise<BudgetReport> {
  const res = await http.get<
    Omit<BudgetReport, "charges"> & {
      charges: Array<Omit<BudgetCharge, "chargeId" | "icon" | "cycles"> & {
        chargeId: number;
        icon: string | null;
        cycles: Array<Omit<BudgetCycleLine, "cycleId"> & { cycleId: number }>;
      }>;
    }
  >("/reports/budget", { dateFrom: period.from, dateTo: period.to });
  return {
    ...res,
    charges: res.charges.map((c) => ({
      ...c,
      chargeId: String(c.chargeId),
      icon: c.icon ?? "ri-price-tag-3-line",
      cycles: c.cycles.map((cy) => ({ ...cy, cycleId: String(cy.cycleId) })),
    })),
  };
}
